import cn from 'classnames';
import { connect } from 'react-redux';

import Pagination from 'components/common/Pagination';
import { WIDTH_840 } from 'components/common/Slideout';
import SlideoutWithRouter from 'components/common/SlideoutWithRouter';
import Loading from 'components/icons/Loading';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import { TEMPO } from 'lib/constants';
import * as Types from 'utilities/types';

import CTABanner from './CTABanner';
import MealCardsList from './MealCardsList';
import MealsNotFound from './MealsNotFound';
import MenuSearch from './MenuSearch';

const MenuSearchSlideout = ({
  closeSlideout,
  handleSearch,
  loading,
  mealPlanSlug,
  needsInitialization,
  numberOfPages,
  onUpdatePage,
  orderableMeals,
  page,
  pastMeals,
  searchTerm,
  showSlideout,
  showUnconvertedLead,
  unmountSlideout,
}) => {
  if (loading || needsInitialization) {
    return (
      <SlideoutWithRouter
        id="meal-search-container-modal-dialog"
        labelId="meal-search-label"
        show={showSlideout}
        desktopWidth={WIDTH_840}
        closeCallback={closeSlideout}
        unmountSlideout={unmountSlideout}
      >
        <Loading className="py-48" />
      </SlideoutWithRouter>
    );
  }

  const noMealsFound = orderableMeals.length + pastMeals.length === 0;
  const noOrderableMealsFound = orderableMeals.length === 0;
  const noOrderableMealsFoundFirstPage = noOrderableMealsFound && page === 1;
  const orderableMealsFound = orderableMeals.length > 0;
  const pastMealsFound = pastMeals.length > 0;

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      if (noOrderableMealsFound && pastMealsFound) {
        logEvent(events.menuSearch.recipeSearchPastResultsViewed, { mealPlanSlug: mealPlanSlug });
      }
    },
    [noOrderableMealsFound, pastMealsFound, mealPlanSlug]
  );

  const renderOrderableMealsList = () => (
    <>
      {orderableMealsFound && (
        <MealCardsList
          meals={orderableMeals}
          title="Available To Order"
          showUnconvertedLead={showUnconvertedLead}
          searchTerm={searchTerm}
          mealPlanSlug={mealPlanSlug}
        />
      )}
      {noOrderableMealsFoundFirstPage && (
        <h3 className="h4 text-center leading-8">
          Our chefs are creating some exciting recipes, stay tuned!
        </h3>
      )}
    </>
  );

  const renderPastMealsList = () => (
    <>
      {pastMealsFound && (
        <MealCardsList
          meals={pastMeals}
          title="From Previous Menus"
          subtitle={`Check out ${searchTerm} recipes that our customers loved!`}
          showUnconvertedLead={showUnconvertedLead}
          searchTerm={searchTerm}
          mealPlanSlug={mealPlanSlug}
        />
      )}
    </>
  );

  const renderMealsFound = () => (
    <>
      <div className="p-4 bp1:p-6">
        {renderOrderableMealsList()}
        {orderableMealsFound && (
          <CTABanner showUnconvertedLead={showUnconvertedLead} mealPlanSlug={mealPlanSlug} />
        )}
      </div>

      <div className="border border-solid border-charcoal-60 bg-charcoal-50 p-4 bp1:p-6">
        {renderPastMealsList()}
        {noOrderableMealsFound && (
          <CTABanner showUnconvertedLead={showUnconvertedLead} mealPlanSlug={mealPlanSlug} />
        )}
        <div className={cn('w-auto bg-charcoal-50', { 'py-6': numberOfPages !== 0 })}>
          <Pagination page={page} pages={numberOfPages} onUpdatePage={onUpdatePage} />
        </div>
      </div>
    </>
  );

  return (
    <SlideoutWithRouter
      id="meal-search-container-modal-dialog"
      labelId="meal-search-label"
      show={showSlideout}
      desktopWidth={WIDTH_840}
      closeCallback={closeSlideout}
      unmountSlideout={unmountSlideout}
    >
      <div className="sticky -top-5 z-one bg-white-100">
        <div className="p-7 bp2:p-6">
          <h1 className="h2 text-charcoal-100">
            {mealPlanSlug === TEMPO ? 'Search Our Meals' : 'Search Our Recipes'}
          </h1>
        </div>
        <div className="border-charcoal-60 bg-white-100 p-3 shadow-md">
          <MenuSearch
            handleSearch={handleSearch}
            searchTerm={searchTerm}
            fieldClass={cn(
              'w-full max-w-lg shadow-md rounded-full h-10 bp2:h-12 border border-solid border-charcoal-60 align-middle',
              { tempo: mealPlanSlug === TEMPO }
            )}
          />
        </div>
      </div>
      {noMealsFound ? (
        <MealsNotFound
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          mealPlanSlug={mealPlanSlug}
        />
      ) : (
        renderMealsFound()
      )}
    </SlideoutWithRouter>
  );
};

const mapStateToProps = state => ({
  needsInitialization: state.menuSearch.needsInitialization,
});

MenuSearchSlideout.propTypes = {
  searchTerm: PropTypes.string,
  showSlideout: PropTypes.bool.isRequired,
  orderableMeals: PropTypes.arrayOf(Types.meal.isRequired),
  pastMeals: PropTypes.arrayOf(Types.meal.isRequired),
  page: PropTypes.number.isRequired,
  numberOfPages: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  showUnconvertedLead: PropTypes.bool.isRequired,
  needsInitialization: PropTypes.bool.isRequired,
  handleSearch: PropTypes.func.isRequired,
  closeSlideout: PropTypes.func.isRequired,
  onUpdatePage: PropTypes.func.isRequired,
  mealPlanSlug: PropTypes.string.isRequired,
  unmountSlideout: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, null)(MenuSearchSlideout);
